import React from 'react';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Input from "@mui/joy/Input";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import Checkbox from "@mui/joy/Checkbox";
import Table from "@mui/joy/Table";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import CircularProgress from "@mui/joy/CircularProgress";
import { PaymentsApi } from './paymentsApi';
import Link from '@mui/joy/Link';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import { PaymentsListRequest, PaymentsListResult, PaymentRow, OperationType, Initializer } from '../../../../models/payments';
import { PaymentMethodsApi } from '../../paymentMethods/paymentMethodsApi';
import { PaymentOperationType, PaymentSystemType } from '../../../../models/paymentMethod';
import { formatRubles } from '../../../../common/money';
import {formatJsonData} from "../../../../common/utils";

function TablesTab() {
    const isDesktop = useMediaQuery('(min-width:600px)');
    const [filtersOpen, setFiltersOpen] = React.useState(false);
    const [operation, setOperation] = React.useState<OperationType>(OperationType.Deposit);
    const [userId, setUserId] = React.useState<string>('');
    const [wallet, setWallet] = React.useState<string>('');
    const [amountMin, setAmountMin] = React.useState<string>('');
    const [amountMax, setAmountMax] = React.useState<string>('');
    const [dateFrom, setDateFrom] = React.useState<string>('');
    const [dateTo, setDateTo] = React.useState<string>('');
    const [paymentMethodId, setPaymentMethodId] = React.useState<number | undefined>(undefined);
    const [statuses, setStatuses] = React.useState<number[]>([2]);
    const [initializer, setInitializer] = React.useState<Initializer | undefined>(Initializer.User);
    const [onlyWithPromoCode, setOnlyWithPromoCode] = React.useState(false);
    const [methods, setMethods] = React.useState<{ id: number; titleRu: string; currencies: string[]; operationType: PaymentOperationType; isActive: boolean }[]>([]);

    const [page, setPage] = React.useState(1);
    const count = 30;
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState<PaymentsListResult | null>(null);
    const [rawEventModal, setRawEventModal] = React.useState<string | null>(null);
    const [feeDetailsModal, setFeeDetailsModal] = React.useState<string | null>(null);

    React.useEffect(() => {
        (async () => {
            const list = await PaymentMethodsApi.getPaymentMethods();
            setMethods(list.map(m => ({ id: m.id, titleRu: m.titleRu, currencies: m.currencies, operationType: m.operationType, isActive: m.isActive })));
        })();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const req: PaymentsListRequest = {
                page,
                count,
                userId: userId || undefined,
                amountRange: amountMin || amountMax ? { min: amountMin ? Math.round(parseFloat(amountMin) * 100) : undefined, max: amountMax ? Math.round(parseFloat(amountMax) * 100) : undefined } : undefined,
                dateRange: dateFrom || dateTo ? { min: dateFrom || undefined, max: dateTo || undefined } : undefined,
                wallet: wallet || undefined,
                paymentMethodId,
                statuses: statuses.length && !statuses.includes(-1) ? statuses : undefined,
                initializer: initializer,
                onlyWithPromoCode: operation === OperationType.Deposit && onlyWithPromoCode ? true : undefined,
            };
            const res = await PaymentsApi.list(operation, req);
            setResult(res);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        load().catch(console.error);
    }, [operation, page, count]);

    const clearFilters = () => {
        setUserId('');
        setWallet('');
        setAmountMin('');
        setAmountMax('');
        setDateFrom('');
        setDateTo('');
        setPaymentMethodId(undefined);
        setStatuses([]);
        setInitializer(Initializer.User);
        setOnlyWithPromoCode(false);
        setPage(1);
    };

    const depositStatuses: { value: number; label: string }[] = [
        { value: -1, label: 'Любой' },
        { value: 0, label: 'Новый' },
        { value: 1, label: 'В обработке' },
        { value: 2, label: 'Оплачен' },
        { value: 3, label: 'Чарджбек' },
        { value: 4, label: 'Рефанд' },
        { value: 5, label: 'Отклонен' },
        { value: 99, label: 'Системная ошибка' },
    ];
    const withdrawStatuses: { value: number; label: string }[] = [
        { value: -1, label: 'Любой' },
        { value: 0, label: 'Новая' },
        { value: 1, label: 'В процессе' },
        { value: 2, label: 'Успешно' },
        { value: 3, label: 'Отклонено' },
        { value: 4, label: 'Возвращено' },
        { value: 5, label: 'Отменено' },
        { value: 99, label: 'Системная ошибка' },
    ];

    const currentStatuses = operation === OperationType.Deposit ? depositStatuses : withdrawStatuses;

    const formatRub = (kopecks: number) => formatRubles(kopecks, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const getSystemTypeName = (systemType: number) => {
        if (systemType === -1) return 'Система';
        switch (systemType) {
            case PaymentSystemType.Tome: return 'Tome';
            case PaymentSystemType.GameMoney: return 'GameMoney';
            default: return systemType?.toString?.() ?? '-';
        }
    };
    const getRowBgColor = (status: number) => {
        const isPending = status === 0 || status === 1; // Новый/Новая или В обработке/В процессе
        const isSuccess = status === 2; // Успешно/Оплачен
        if (isPending) return '#FFF7D6'; // мягкий жёлтый
        if (isSuccess) return '#EAF7EA'; // мягкий зелёный
        return '#FDEAEA'; // мягкий красный для остальных
    };
    const pad2 = (n: number) => n.toString().padStart(2, '0');
    const formatDateTime = (d: Date) => {
        const dd = pad2(d.getDate());
        const mm = pad2(d.getMonth() + 1);
        const yy = pad2(d.getFullYear() % 100);
        const hh = pad2(d.getHours());
        const mi = pad2(d.getMinutes());
        const ss = pad2(d.getSeconds());
        return `${dd}.${mm}.${yy} ${hh}:${mi}:${ss}`;
    };
    const formatTime = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
    const formatDateWithCompletionTime = (createdAt?: string, completedAt?: string) => {
        const created = createdAt ? new Date(createdAt) : null;
        const completed = completedAt ? new Date(completedAt) : null;
        if (!created) return '-';
        const createdStr = formatDateTime(created);
        if (!completed) return createdStr;
        const timeStr = formatTime(completed);
        return `${createdStr} (${timeStr})`;
    };
    const getStatusDotColor = (status: number) => getRowBgColor(status);

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography level="h4">
                Таблицы пополнений и выводов
            </Typography>

            {/* Фильтры */}
            <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: '8px' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <FormControl size="sm" sx={{ minWidth: { xs: 0, sm: 160 }, width: { xs: '100%', sm: 'auto' } }}>
                        <FormLabel>Тип операции</FormLabel>
                        <Select value={operation} onChange={(_, v) => v && setOperation(v as OperationType)}>
                            <Option value={OperationType.Deposit}>Пополнение</Option>
                            <Option value={OperationType.Withdraw}>Вывод</Option>
                        </Select>
                    </FormControl>

                    {!isDesktop && (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                            <Button size="sm" variant="outlined" onClick={() => setFiltersOpen(o => !o)}>
                                {filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
                            </Button>
                        </Box>
                    )}
                </Box>

                <Collapse in={isDesktop || filtersOpen}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                        <FormControl size="sm" sx={{ minWidth: { xs: 0, sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                            <FormLabel>Юзер ID</FormLabel>
                            <Input fullWidth value={userId} onChange={e => setUserId(e.target.value)} placeholder="12345" />
                        </FormControl>

                        <FormControl size="sm" sx={{ minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                            <FormLabel>Кошелёк</FormLabel>
                            <Input fullWidth value={wallet} onChange={e => setWallet(e.target.value)} placeholder="wallet" />
                        </FormControl>

                        <FormControl size="sm" sx={{ minWidth: { xs: 0, sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
                            <FormLabel>Сумма (₽)</FormLabel>
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Input fullWidth value={amountMin} onChange={e => setAmountMin(e.target.value)} placeholder="от" />
                                <Input fullWidth value={amountMax} onChange={e => setAmountMax(e.target.value)} placeholder="до" />
                            </Box>
                        </FormControl>

                        <FormControl size="sm" sx={{ minWidth: { xs: 0, sm: 260 }, width: { xs: '100%', sm: 'auto' } }}>
                            <FormLabel>Диапазон дат</FormLabel>
                            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Input fullWidth type="datetime-local" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                <Input fullWidth type="datetime-local" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                            </Box>
                        </FormControl>

                        <FormControl size="sm" sx={{ minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                            <FormLabel>Метод оплаты</FormLabel>
                            <Select sx={{ width: { xs: '100%', sm: 'auto' } }} value={(paymentMethodId ?? '').toString()} onChange={(_, v) => setPaymentMethodId(v ? Number(v) : undefined)} placeholder="Все">
                                <Option value="">Все</Option>
                                {methods
                                    .filter(m => (operation === OperationType.Deposit ? m.operationType === PaymentOperationType.Deposit : m.operationType === PaymentOperationType.Withdraw))
                                    .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.titleRu.localeCompare(b.titleRu))
                                    .map(m => (
                                    <Option key={m.id} value={m.id.toString()} sx={{ color: m.isActive ? undefined : 'danger.600' }}>{m.titleRu} {m.currencies?.join(' ')}</Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="sm" sx={{ minWidth: { xs: 0, sm: 240 }, width: { xs: '100%', sm: 'auto' } }}>
                            <FormLabel>Статусы</FormLabel>
                            <Select
                                multiple
                                value={statuses}
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                                onChange={(_, v) => {
                                    const values = (v as number[]) ?? [];
                                    if (values.includes(-1)) {
                                        setStatuses([-1]);
                                    } else {
                                        setStatuses(values);
                                    }
                                }}
                                placeholder="Все"
                            >
                                {currentStatuses.map(s => (
                                    <Option key={s.value} value={s.value}>{s.label}</Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="sm" sx={{ minWidth: { xs: 0, sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
                            <FormLabel>Типы</FormLabel>
                            <Select
                                value={(initializer ?? Initializer.User).toString()}
                                onChange={(_, v) => {
                                    if (v === null) return;
                                    setInitializer(Number(v) as Initializer);
                                }}
                            >
                                <Option value={Initializer.User.toString()}>Пользовательские</Option>
                                <Option value={Initializer.Admin.toString()}>Внутренние</Option>
                            </Select>
                        </FormControl>

                        {operation === OperationType.Deposit && (
                            <FormControl size="sm" sx={{ justifyContent: 'flex-end' }}>
                                <Checkbox
                                    size="sm"
                                    label="Только с промокодом"
                                    checked={onlyWithPromoCode}
                                    onChange={(e) => setOnlyWithPromoCode(e.target.checked)}
                                />
                            </FormControl>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', width: { xs: '100%', sm: 'auto' } }}>
                            <Button size="sm" onClick={load} sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, width: { xs: '100%', sm: 'auto' } }}>Применить</Button>
                            <Button size="sm" variant="outlined" onClick={clearFilters} sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' }, width: { xs: '100%', sm: 'auto' } }}>Сбросить</Button>
                        </Box>
                    </Box>
                </Collapse>
            </Box>

            {/* Таблица */}
            <Box sx={{ display: { xs: 'none', md: 'grid' }, gap: 1 }}>

                {loading && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.body', opacity: 0.6 }}>
                        <CircularProgress />
                    </Box>
                )}
                <Table hoverRow sx={{ '& th': { whiteSpace: 'nowrap' }, tableLayout: 'fixed' }}>
                    <colgroup>
                        <col style={{ width: 70 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 220 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 200 }} />
                        <col style={{ width: 90 }} />
                        <col style={{ width: 170 }} />
                        <col style={{ width: 80 }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Пользователь</th>
                            <th>Метод</th>
                            <th>Платёжная система</th>
                            <th>Статус</th>
                            <th>Сумма</th>
                            <th>Валюта</th>
                            <th>Дата</th>
                            <th>Инфо</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(result?.items ?? []).map((row: PaymentRow) => (
                            <tr key={row.id} style={{ backgroundColor: getRowBgColor(row.status) }}>
                                <td>{row.id}</td>
                                <td>
                                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Link underline="hover" href={`/app/users/${row.userId.toString()}`} target="_blank">{row.userId.toString()}</Link>
                                    </Box>
                                </td>
                                <td>
                                    {row.paymentMethodTitle}
                                    {((): string => {
                                        const method = methods.find(m => m.id === row.paymentMethodId);
                                        const currencies = method?.currencies?.join(' ');
                                        return currencies ? ` ${currencies}` : '';
                                    })()}
                                </td>
                                <td>{getSystemTypeName(row.systemType)}</td>
                                <td>{operation===OperationType.Deposit?depositStatuses.filter(s=>s.value=== row.status)[0].label:withdrawStatuses.filter(s=>s.value=== row.status)[0].label}</td>
                                <td>
                                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                        {operation === OperationType.Deposit ? 'Зачисленно в ST: ' : 'Списано в ST: '}
                                        <Box component="span" sx={{ fontWeight: 'lg', color: 'text.primary' }}>
                                            {formatRub(row.amount)}
                                        </Box>
                                    </Typography>
                                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                        {`Наша прибыль: ${formatRub(row.ourBenefit)}`}
                                    </Typography>
                                    {row.promoCode && (
                                        <Chip size="sm" variant="soft" color="success">
                                            {`Промокод ${row.promoCode}: +${formatRub(row.promoBonusAmount ?? 0)}`}
                                        </Chip>
                                    )}
                                </td>
                                <td>{row.currency}</td>
                                <td>{formatDateWithCompletionTime(row.createdAt, row.completedAt)}</td>
                                <td>
                                    {row.rawEvent && row.rawEvent.trim() ? (
                                        <Button size="sm" variant="outlined" onClick={() => setRawEventModal(row.rawEvent)}>i</Button>
                                    ) : null}
                                    {row.feeDetails && row.feeDetails.trim() ? (
                                        <Button size="sm" variant="outlined" onClick={() => setFeeDetailsModal(row.feeDetails)}>$</Button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Typography level="body-sm">Всего: {result?.total ?? 0}</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="sm" variant="outlined" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Назад</Button>
                        <Typography level="body-sm" sx={{ alignSelf: 'center' }}>{page} / {Math.max(1, Math.ceil((result?.total ?? 0) / count))}</Typography>
                        <Button size="sm" variant="outlined" disabled={page >= Math.max(1, Math.ceil((result?.total ?? 0) / count))} onClick={() => setPage(p => p + 1)}>Вперёд</Button>
                    </Box>
                </Box>
            </Box>

            {/* Мобильный вид */}
            <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1 }}>
                {(result?.items ?? []).map((row: PaymentRow) => {
                    return (
                        <Box key={row.id}
                             sx={{p: 1.5, border: '1px солид', borderColor: 'divider', borderRadius: '8px'}}>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusDotColor(row.status) }} />
                                    <Typography level="title-sm">#{row.id}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {row.rawEvent && row.rawEvent.trim() ? (
                                        <Button size="sm" variant="outlined" onClick={() => setRawEventModal(row.rawEvent)}>i</Button>
                                    ) : null}
                                    {row.feeDetails && row.feeDetails.trim() ? (
                                        <Button size="sm" variant="outlined" onClick={() => setFeeDetailsModal(row.feeDetails)}>$</Button>
                                    ) : null}
                                    <Typography level="body-sm">{formatDateWithCompletionTime(row.createdAt, row.completedAt)}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5}}>
                                <Chip size="sm">
                                    Пользователь: <Link underline="hover" href={`/app/users/${row.userId.toString()}`} target="_blank">{row.userId.toString()}</Link>
                                </Chip>
                                <Chip size="sm">Метод: {row.paymentMethodTitle}{((): string => {
                                    const method = methods.find(m => m.id === row.paymentMethodId);
                                    const currencies = method?.currencies?.join(' ');
                                    return currencies ? ` ${currencies}` : '';
                                })()}</Chip>
                                <Chip size="sm">Платёжная система: {getSystemTypeName(row.systemType)}</Chip>
                                <Chip size="sm">Статус: {operation===OperationType.Deposit?depositStatuses.filter(s=>s.value=== row.status)[0].label:withdrawStatuses.filter(s=>s.value=== row.status)[0].label}</Chip>
                                <Chip size="sm">{operation===OperationType.Deposit?`Зачисленно в ST: ${formatRub(row.amount)}`:`Списано в ST: ${formatRub(row.amount)}`}</Chip>
                                <Chip size="sm">Наша прибыль: {formatRub(row.ourBenefit)}</Chip>
                                {row.promoCode && (
                                    <Chip size="sm" variant="soft" color="success">
                                        {`Промокод ${row.promoCode}: +${formatRub(row.promoBonusAmount ?? 0)}`}
                                    </Chip>
                                )}
                                <Chip size="sm">Валюта: {row.currency}</Chip>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Модалка: событие от платежной системы */}
            <Modal open={!!rawEventModal} onClose={() => setRawEventModal(null)}>
                <ModalDialog>
                    <DialogTitle>Событие от платежной системы</DialogTitle>
                    <DialogContent>
                        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {formatJsonData(rawEventModal)}
                        </Box>
                    </DialogContent>
                </ModalDialog>
            </Modal>

            {/* Модалка: отображающая комисии и информацию о суммах платежей */}
            <Modal open={!!feeDetailsModal} onClose={() => setFeeDetailsModal(null)}>
                <ModalDialog>
                    <DialogTitle>Информация о платеже</DialogTitle>
                    <DialogContent>
                        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {formatJsonData(feeDetailsModal)}
                        </Box>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </Box>
    );
}

export default TablesTab
